/* eslint-disable class-methods-use-this */
/* eslint-disable max-classes-per-file */
/**
 * @module cercaliagl/MapDistance
 */

import Feature from './Feature';
import Marker from './Marker';

import { IMG } from './globals';
import { isMobile } from './util';
import MapControls from './control/MapControls';
import Distance from './service/Distance';
import LonLat from './LonLat';
import Icon from './Icon';
import SimpleLabel from './SimpleLabel';
import GeometryType from './GeometryType';

/**
 * @classdesc
 */
class MapDistance {
  /**
   * @constructor
   */
  constructor() {
    /**
   * @private
   * @type {ol.interaction.Draw|null}
   */
    this.measureInteraction_ = null;

    /**
      * @private
      * @type {import("./Map").default}
    */
    this.map_ = null;

    /**
     * @protected
     * @type {import("./layer/Base").default}
     */
    this.layer_ = null;

    /**
  * @private
  * @type {import("./service/Distance").default}
  */
    this.distanceService_ = null;

    /**
     * @private
     * @type {Array.<Marker>}
     */
    this.markers_ = [];

    /**
     * @private
     * @type {Array.<Feature>}
     */
    this.features_ = [];
  }

  /**
 * Clean
 * @api
 */
  clean() {
    if (this.markers_.length > 0) {
      this.map_.removeMarkers(this.markers_);
      this.markers_ = [];
    }
    if (this.features_.length > 0) {
      this.map_.removeFeatures(this.features_);
      this.features_ = [];
    }
  }

  /**
 * Enable draw interaction
 * @api
 * @param {string} type Type, available types: `line, road, area`
 */
  enableDistanceInteraction(type) {
    if (isMobile) {
      let drawType;
      let onDrawEnd;
      let weight;

      if (type === 'line') {
        onDrawEnd = (feature) => this.distanceInteractionCalcLine_(feature);
        drawType = 'line';
        weight = 'line';
      } else if (type === 'road') {
        onDrawEnd = (feature) => this.distanceInteractionCalcDistance_(feature);
        drawType = 'line';
        weight = 'distance';
      } else if (type === 'area') { // area
        onDrawEnd = (feature) => this.distanceInteractionCalcArea_(feature);
        drawType = 'polygon';
        weight = 'area';
      } else {
        return;
      }
      this.distanceService_ = new Distance({
        weight,
        calculateGeometry: true,
      });

      const mbMap = this.map_.getMapboxglMap();
      const drawControl = /** @type {import("./control/Draw").default} */ (this.map_.getControlByName(MapControls.DRAW));
      if (drawType === 'line') {
        drawControl.setDrawLinestring();
      } else if (drawType === 'polygon') {
        drawControl.setDrawPolygonMode();
      } else {
        console.warn(`Unknown type: ${type}`); // eslint-disable-line no-console
        return;
      }
      mbMap.once('draw.create', (e) => {
        drawControl.deleteFeature(e.features[0].id);
        if (e.features[0]) {
          onDrawEnd(e.features[0]);
        }
        setTimeout(() => drawControl.setStaticMode(), 50);
      });
    } else {
      console.warn('Feature disabled for mobile'); // eslint-disable-line no-console
    }

    // this.map_.enableDrawInteraction(drawType, {
    //   onDrawEnd,
    // });
  }

  /**
   * @param {import('./Map').default} map Map
   * @api
   */
  setMap(map) {
    this.map_ = map;
  }

  /**
 * @private
 * @param {Feature} Feature Drawn feature
 */
  distanceInteractionCalcLine_(feature) {
    const markers = [];
    const numberOfPoints = feature.geometry.coordinates.length;
    // const distance = Math.round(Distance.calculateDistanceOnLine(feature.geometry) * 100) / 100;
    const distance = Distance.calculateDistanceOnLine(feature.geometry);
    const origin = feature.geometry.coordinates[0];
    const final = feature.geometry.coordinates[feature.geometry.coordinates.length - 1];

    const markerOrigin = new Marker({
      position: new LonLat(origin[0], origin[1]),
      icon: new Icon({
        src: `${IMG}/point.png`,
      }),
    });
    markers.push(markerOrigin);

    const geometryPointsCalculated = { type: GeometryType.LINESTRING, coordinates: [feature.geometry.coordinates[0]] };
    for (let i = 1; i < (numberOfPoints - 1); i += 1) {
      const currentPoint = feature.geometry.coordinates[i];
      geometryPointsCalculated.coordinates.push(currentPoint);
      const currentDistance = Distance.calculateDistanceOnLine(geometryPointsCalculated);
      let finalDistance = Math.round(currentDistance * 100) / 100;
      let simpleLabelUnit = 'km';
      if (finalDistance < 1) {
        finalDistance = Math.round(finalDistance * 1000);
        simpleLabelUnit = 'm';
      }
      const currentMarker = new Marker({
        position: new LonLat(currentPoint[0], currentPoint[1]),
        icon: new Icon({
          src: `${IMG}/point.png`,
        }),
        simpleLabel: new SimpleLabel({
          text: ` ${finalDistance} ${simpleLabelUnit}`,
        }),
      });
      markers.push(currentMarker);
    }
    let finalDistance = Math.round(distance * 100) / 100;
    let simpleLabelUnit = 'km';
    if (finalDistance < 1) {
      finalDistance = Math.round(finalDistance * 1000);
      simpleLabelUnit = 'm';
    }
    const markerFinal = new Marker({
      position: new LonLat(final[0], final[1]),
      icon: new Icon({
        src: `${IMG}/point.png`,
      }),
      simpleLabel: new SimpleLabel({
        text: `${finalDistance} ${simpleLabelUnit}`,
      }),
    });

    markers.push(markerFinal);

    const featureGeo = Feature.fromGeoJSON(feature, {
      outlineOpacity: 0.01,
      strokeWidth: 2,
      strokeColor: '#404040',
    });

    this.map_.addFeature(featureGeo);
    this.features_.push(featureGeo);
    this.map_.addMarkers(markers);
    this.markers_.push(...markers);
  }

  /**
 * @private
 * @param {Feature} Feature Drawn feature
 */
  distanceInteractionCalcArea_(feature) {
    const distance = Math.round((Distance.calculateDistanceOnArea(feature.geometry) / 10000) * 100) / 100;
    const featureGeo = Feature.fromGeoJSON(feature, {
      outlineOpacity: 0.01,
      strokeWidth: 2,
      strokeColor: '#404040',
      fillColor: '#404040',
    });
    const center = featureGeo.getBounds().getCenterLonLat();
    const marker = new Marker({
      position: new LonLat(center.getLon(), center.getLat()),
      icon: new Icon({
        src: `${IMG}/point.png`,
      }),
      simpleLabel: new SimpleLabel({
        text: `${distance} ha`,
      }),
    });

    this.map_.addMarker(marker);
    this.map_.addFeature(featureGeo);
    this.features_.push(featureGeo);
    this.markers_.push(marker);
  }

  /**
 * @private
 * @param {Feature} Feature Drawn feature
 */
  distanceInteractionCalcDistance_(feature) {
    const featureGeo = Feature.fromGeoJSON(feature, undefined);
    this.distanceService_.setDataFromGeometry(featureGeo.getGeometry());
    this.distanceService_.calculate((data) => this.distanceInteractionCalcDistanceCallback_(data));
  }

  /**
 * @private
 * @param {Object} data Result of distance service calculate
 */
  distanceInteractionCalcDistanceCallback_(data) {
    const listStops = data.cercalia.route.stoplist.stop;
    const stages = data.cercalia.route.stages.stage;
    const wkt = data.cercalia.geometry.route[0].wkt.value;
    const feature = new Feature({
      wkt,
      outlineOpacity: 0.01,
      strokeWidth: 2,
      strokeColor: '#404040',
    });

    this.map_.addFeature(feature);
    this.features_.push(feature);

    let countKm = 0;

    for (let i = 0; i < listStops.length; i += 1) {
      let marker;
      const coordX = parseFloat(listStops[i].mo.coord.x);
      const coordY = parseFloat(listStops[i].mo.coord.y);
      const icon = new Icon({
        src: `${IMG}/point.png`,
      });

      if (i === 0) {
        marker = new Marker({
          icon,
          position: new LonLat(coordX, coordY),
        });
      } else {
        countKm += parseFloat(stages[i - 1].dist);
        countKm = Math.round(countKm * 100) / 100;
        const textLabel = countKm < 1 ? `${Math.round(countKm * 1000)} m` : `${countKm} km`;
        marker = new Marker({
          icon,
          position: new LonLat(coordX, coordY),
          simpleLabel: new SimpleLabel({
            text: textLabel,
          }),
        });
      }
      this.map_.addMarker(marker);
      this.markers_.push(marker);
    }
  }
}

export default MapDistance;
